// =============================================================================
// pnlExcel.js
// Exports the income statement to .xlsx, laid out the way the page lays it out:
// section headings, indented detail, subtotals in the right places.
//
// TWO DELIBERATE DEPARTURES FROM "EXPORT WHAT I SEE":
//
//   1. ALWAYS ALL TWELVE MONTHS. The drill-down (Full Year → quarters → months)
//      is a reading aid for a screen with finite width. A spreadsheet has no
//      such limit, and a workbook that quietly contains one "Full Year" column
//      because that is how the page happened to be set is a worse artefact than
//      one that is slightly wider than you wanted.
//   2. ALWAYS ALL DETAIL LINES. Folding a section away is a reading aid too.
//      Exporting a collapsed section would silently drop lines from a document
//      somebody is about to send to a board, which is the single worst thing
//      this file could do.
//
// The ISSUES sheet travels with the numbers for the same reason the page shows
// them: unposted cost and budget stranded on balance-sheet accounts change what
// the total means, and a spreadsheet emailed onwards is exactly where that
// context normally gets lost.
// =============================================================================
import ExcelJS from 'exceljs';
import { PERIODS, periodLabel } from './financialPeriods';

// Accounting format: thousands separated, negatives in brackets, dash for zero.
const MONEY = '#,##0;(#,##0);"—"';
const PCT = '0.0%;(0.0%);"—"';

const BLACK = 'FF000000';
const SECTION_FILL = 'FFE2E8F0';
const SUBTOTAL_FILL = 'FFDBEAFE';
const GRAND_FILL = 'FFD1FAE5';
const GRAND_FILL_NEG = 'FFFEE2E2';

const safeName = (s) => String(s || 'version').replace(/[^a-z0-9_-]+/gi, '_');

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const GRAIN_LABEL = { account: 'Account', module: 'Module', site: 'Site' };

/**
 * @param versionLabel  budget version, e.g. "V2"
 * @param grain         'account' | 'module' | 'site'
 * @param statement     rows from the page, collapse ignored:
 *                      { kind: 'section'|'line'|'subtotal', label, code?, group?,
 *                        values: number[12], total, grand? }
 * @param grossRevenue  denominator for the % column (0 = no % shown). GROSS,
 *                      not net — the page commons-sizes on gross revenue so
 *                      the discount line reads as the rate that was actually
 *                      negotiated. The workbook must match the screen.
 * @param fyStartMonth  tenant financial-year start, for the month headings
 * @param issues        [{ level, title, body, detail, fix }] — may be empty
 */
export async function exportPnl({
  versionLabel, grain, statement, grossRevenue = 0, fyStartMonth, issues = [],
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet('Profit & Loss', {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 6 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  // ---- Preamble. Anyone opening this six months from now needs to know which
  // version it is and which way the signs run before they read a single figure.
  ws.getCell('A1').value = 'Profit & Loss';
  ws.getCell('A1').font = { bold: true, size: 16 };
  ws.getCell('A2').value = `Budget version ${versionLabel} · detail by ${GRAIN_LABEL[grain]?.toLowerCase() || grain}`;
  ws.getCell('A2').font = { size: 11, color: { argb: 'FF475569' } };
  ws.getCell('A3').value = 'Income positive, costs in brackets — every subtotal is the sum of the lines above it.';
  ws.getCell('A3').font = { size: 10, italic: true, color: { argb: 'FF64748B' } };
  ws.getCell('A4').value = `Exported ${new Date().toLocaleString()} from Mantle`
    + (issues.length ? ` · ${issues.length} unresolved issue${issues.length === 1 ? '' : 's'} — see the Issues sheet` : '');
  ws.getCell('A4').font = {
    size: 10,
    color: { argb: issues.some((i) => i.level === 'error') ? 'FFB91C1C' : 'FF64748B' },
    bold: issues.length > 0,
  };

  // ---- Header row (row 6) -------------------------------------------------
  const HEADER_ROW = 6;
  const headings = [
    GRAIN_LABEL[grain] || 'Line',
    'Group',
    ...PERIODS.map((p) => periodLabel(p, fyStartMonth)),
    'Total',
    '% of Gross Revenue',
  ];
  const header = ws.getRow(HEADER_ROW);
  headings.forEach((h, i) => { header.getCell(i + 1).value = h; });
  header.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  header.alignment = { vertical: 'middle', horizontal: 'right' };
  header.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
  header.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  header.height = 20;
  headings.forEach((_, i) => {
    header.getCell(i + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLACK } };
  });

  ws.columns = [
    { width: 46 }, { width: 24 },
    ...PERIODS.map(() => ({ width: 14 })),
    { width: 16 }, { width: 17 },
  ];

  // ---- Body ---------------------------------------------------------------
  const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

  statement.forEach((r, idx) => {
    const row = ws.getRow(HEADER_ROW + 1 + idx);

    // Indentation carries the hierarchy in a spreadsheet the same way it does
    // on the page — the detail must read as subordinate to its section.
    row.getCell(1).value = r.kind === 'section' ? r.label.toUpperCase()
      : r.kind === 'subtotal' ? r.label.toUpperCase()
        : `${r.code && r.code !== r.label ? `${r.code}  ` : ''}${r.label}`;
    if (r.kind === 'line') row.getCell(1).alignment = { indent: 2 };
    if (r.kind === 'line') row.getCell(2).value = r.group || '';

    PERIODS.forEach((p, i) => {
      const c = row.getCell(3 + i);
      c.value = Math.round(r.values[p - 1] || 0);
      c.numFmt = MONEY;
    });
    const totalCell = row.getCell(3 + PERIODS.length);
    totalCell.value = Math.round(r.total || 0);
    totalCell.numFmt = MONEY;

    const pctCell = row.getCell(4 + PERIODS.length);
    // A real number formatted as a percentage, not a string — the recipient
    // will want to sort and chart on it.
    pctCell.value = grossRevenue ? (r.total / grossRevenue) : null;
    pctCell.numFmt = PCT;

    if (r.kind === 'section') {
      row.font = { bold: true };
      row.eachCell({ includeEmpty: true }, (c) => { c.fill = fill(SECTION_FILL); });
    } else if (r.kind === 'subtotal') {
      row.font = { bold: true, size: r.grand ? 12 : 11 };
      const bg = r.grand ? (r.total >= 0 ? GRAND_FILL : GRAND_FILL_NEG) : SUBTOTAL_FILL;
      row.eachCell({ includeEmpty: true }, (c) => {
        c.fill = fill(bg);
        c.border = { top: { style: 'thin' }, ...(r.grand ? { bottom: { style: 'double' } } : {}) };
      });
    }
  });

  ws.autoFilter = undefined; // a statement is ordered; filtering it breaks the subtotals

  // ---- Issues sheet -------------------------------------------------------
  if (issues.length) {
    const is = wb.addWorksheet('Issues', { views: [{ state: 'frozen', ySplit: 1 }] });
    is.columns = [
      { header: 'Severity', key: 'lvl', width: 12 },
      { header: 'Issue', key: 'title', width: 56 },
      { header: 'What it means', key: 'body', width: 78 },
      { header: 'Detail', key: 'detail', width: 78 },
      { header: 'How to fix it', key: 'fix', width: 78 },
    ];
    const h = is.getRow(1);
    h.font = { bold: true, color: { argb: 'FFF8FAFC' } };
    h.eachCell((c) => { c.fill = fill(BLACK); });
    issues.forEach((i) => {
      const row = is.addRow({
        lvl: i.level === 'error' ? 'MISSING' : 'CHECK',
        title: i.title, body: i.body, detail: i.detail || '', fix: i.fix || '',
      });
      row.alignment = { vertical: 'top', wrapText: true };
      row.getCell(1).font = { bold: true, color: { argb: i.level === 'error' ? 'FFB91C1C' : 'FFB45309' } };
    });
  }

  const buf = await wb.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Mantle_PnL_${safeName(versionLabel)}_${grain}.xlsx`
  );
}

export default exportPnl;
